import React, { useMemo, useState } from 'react';
import { DatePicker as MobileDatePicker, Picker as MobilePicker } from 'antd-mobile';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

const pad2 = (n) => String(n).padStart(2, '0');

// 从 dayjs format 推断需要哪些时间列：H/h=时, m=分, s=秒
function getTimeParts(format) {
    return {
        hour: /[Hh]/.test(format),
        minute: /m/.test(format),
        second: /s/.test(format)
    };
}

// blockPicker.dateType -> antd-mobile DatePicker 的 precision
export function getDatePrecision(dateType, format) {
    if (dateType === 'showTime') {
        const p = getTimeParts(format);
        if (p.second) return 'second';
        if (p.minute) return 'minute';
        if (p.hour) return 'hour';
        return 'second';
    }
    if (dateType === 'year') return 'year';
    if (dateType === 'month') return 'month';
    if (dateType === 'week') return 'week';
    if (dateType === 'quarter') return 'quarter';
    return 'day';
}

// 防御性解析：先用 format 解析（依赖 customParseFormat 插件），失败再退回默认解析
function safeDayjs(val, format) {
    if (val === undefined || val === null || val === '') return null;
    let d = dayjs(val, format);
    if (!d.isValid()) d = dayjs(val);
    if (!d.isValid()) return null;
    return d;
}

// 将值（dayjs 对象 / 字符串 / null）按 format 格式化为字符串；无法解析返回 ''
// 用于触发框显示以及同步 allFieldValues（与 PC 端 onChange 的 dateString 行为一致）
function formatValue(val, format) {
    const d = safeDayjs(val, format);
    return d ? d.format(format) : '';
}

// 触发框：样式模仿 antd 的 picker 输入框（边框 + 文本 + 右侧图标）
const TriggerBox = styled.div`
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    cursor: pointer;
    user-select: none;
    line-height: 1.5;
    &.is-disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
    .mpf-text {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
    .mpf-placeholder {
        color: rgba(0, 0, 0, 0.25);
    }
    .mpf-icon {
        flex-shrink: 0;
        margin-left: 8px;
        color: rgba(0, 0, 0, 0.25);
        display: inline-flex;
        align-items: center;
    }
`;

function TriggerSegment({ style, text, placeholder, icon, disabled, onClick }) {
    const borderColor = (style && style.borderColor) || '#d9d9d9';
    const rawWidth = (style && style.borderWidth != null) ? style.borderWidth : 1;
    const borderWidth = typeof rawWidth === 'number' ? rawWidth + 'px' : rawWidth;
    const boxStyle = {
        ...style,
        borderStyle: 'solid',
        borderColor,
        borderWidth,
        minHeight: style && style.fontSize ? Math.round(style.fontSize * 2.4) : 32,
        display: 'flex',
        alignItems: 'center',
        padding:' 4px 11px 4px',
        borderRadius: '6px',
        boxSizing: 'border-box'
    };
    return (
        <TriggerBox className={disabled ? 'is-disabled' : ''} style={boxStyle} onClick={disabled ? undefined : onClick}>
            <span className={'mpf-text' + (text ? '' : ' mpf-placeholder')}>{text || placeholder}</span>
            <span className="mpf-icon">{icon}</span>
        </TriggerBox>
    );
}

function RangeRow({ style, v0, v1, ph0, ph1, icon, disabled, onPickStart, onPickEnd }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <TriggerSegment style={style} text={v0} placeholder={ph0} icon={icon} disabled={disabled} onClick={onPickStart} />
            </div>
            <span style={{ color: 'rgba(0,0,0,0.45)' }}>~</span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <TriggerSegment style={style} text={v1} placeholder={ph1} icon={icon} disabled={disabled} onClick={onPickEnd} />
            </div>
        </div>
    );
}

/**
 * 日期/日期时间字段（用于 blockPicker）。
 * 作为 Form.Item 直接子元素，由 Form.Item 注入 value/onChange。
 * 额外配置通过显式 props 传入（不被 Form.Item 覆盖）。
 */
export function MobileDateField(props) {
    const { value, onChange, onValue, format, precision, min, max, placeholders, style, disabled, isRange, title } = props;
    const [visible, setVisible] = useState(false);
    const [rangeSide, setRangeSide] = useState('start');

    // 无 min/max 时给一个足够宽的可选范围（覆盖生日等场景）
    const minDate = useMemo(() => (min ? min.toDate() : new Date(new Date().getFullYear() - 100, 0, 1)), [min]);
    const maxDate = useMemo(() => (max ? max.toDate() : new Date(new Date().getFullYear() + 100, 11, 31)), [max]);

    const currentDate = useMemo(() => {
        if (isRange) {
            const v = value && value[rangeSide === 'start' ? 0 : 1];
            return safeDayjs(v, format);
        }
        return safeDayjs(value, format);
    }, [value, format, isRange, rangeSide]);

    // range 模式下，结束日期的最小值为开始日期
    const pickerMin = useMemo(() => {
        if (isRange && rangeSide === 'end' && value && value[0]) {
            const d = safeDayjs(value[0], format);
            if (d) return d.toDate();
        }
        return minDate;
    }, [isRange, rangeSide, value, format, minDate]);

    const openPicker = (side) => {
        if (isRange) setRangeSide(side);
        setVisible(true);
    };

    const handleConfirm = (date) => {
        const d = dayjs(date);
        if (isRange) {
            const side = rangeSide === 'start' ? 0 : 1;
            // Form 字段：dayjs 数组（与 PC RangePicker 的 dates 一致，供 getFieldValue 后 .format() 调用）
            const formArr = Array.isArray(value) && value.length === 2 ? [...value] : [null, null];
            formArr[side] = d;
            // allFieldValues：字符串数组（与 PC onChange 的 dateStrings 一致）
            const otherIdx = 1 - side;
            const other = Array.isArray(value) ? value[otherIdx] : null;
            const storeArr = ['', ''];
            storeArr[side] = d.format(format);
            storeArr[otherIdx] = formatValue(other, format);
            onChange && onChange(formArr);
            onValue && onValue(storeArr);
        } else {
            // Form 字段：dayjs 对象（与 PC DatePicker 的 date 一致）
            onChange && onChange(d);
            // allFieldValues：字符串（与 PC onChange 的 dateString 一致）
            onValue && onValue(d.format(format));
        }
        setVisible(false);
    };

    const ph0 = (placeholders && placeholders[0]) || '';
    const ph1 = (placeholders && placeholders[1]) || '';

    return (
        <>
            {isRange ? (
                <RangeRow
                    style={style}
                    v0={formatValue(value?.[0], format)}
                    v1={formatValue(value?.[1], format)}
                    ph0={ph0}
                    ph1={ph1}
                    icon={<CalendarOutlined />}
                    disabled={disabled}
                    onPickStart={() => openPicker('start')}
                    onPickEnd={() => openPicker('end')}
                />
            ) : (
                <TriggerSegment
                    style={style}
                    text={formatValue(value, format)}
                    placeholder={ph0}
                    icon={<CalendarOutlined />}
                    disabled={disabled}
                    onClick={() => openPicker('start')}
                />
            )}
            <MobileDatePicker
                visible={visible}
                value={currentDate ? currentDate.toDate() : new Date()}
                precision={precision}
                min={pickerMin}
                max={maxDate}
                title={title}
                onClose={() => setVisible(false)}
                onConfirm={handleConfirm}
            />
        </>
    );
}

/**
 * 时间字段（用于 blockTimePicker）。
 * 用 antd-mobile 的 Picker 渲染时/分/秒各自的滚轮列。
 */
export function MobileTimeField(props) {
    const { value, onChange, onValue, format, placeholders, style, disabled, isRange, title } = props;
    const [visible, setVisible] = useState(false);
    const [rangeSide, setRangeSide] = useState('start');

    const parts = useMemo(() => getTimeParts(format), [format]);

    const columns = useMemo(() => {
        const cols = [];
        if (parts.hour) cols.push(Array.from({ length: 24 }, (_, i) => ({ label: pad2(i), value: i })));
        if (parts.minute) cols.push(Array.from({ length: 60 }, (_, i) => ({ label: pad2(i), value: i })));
        if (parts.second) cols.push(Array.from({ length: 60 }, (_, i) => ({ label: pad2(i), value: i })));
        return cols;
    }, [parts]);

    const currentValueArray = useMemo(() => {
        let d;
        if (isRange) {
            const v = value && value[rangeSide === 'start' ? 0 : 1];
            d = safeDayjs(v, format);
        } else {
            d = safeDayjs(value, format);
        }
        if (!d) d = dayjs(); // 默认当前时间
        const arr = [];
        if (parts.hour) arr.push(d.hour());
        if (parts.minute) arr.push(d.minute());
        if (parts.second) arr.push(d.second());
        return arr;
    }, [value, format, isRange, rangeSide, parts]);

    const openPicker = (side) => {
        if (isRange) setRangeSide(side);
        setVisible(true);
    };

    const handleConfirm = (arr) => {
        let d = dayjs();
        let idx = 0;
        if (parts.hour) d = d.hour(Number(arr[idx++]));
        if (parts.minute) d = d.minute(Number(arr[idx++]));
        if (parts.second) d = d.second(Number(arr[idx++]));
        const formatted = d.format(format);
        if (isRange) {
            const side = rangeSide === 'start' ? 0 : 1;
            // Form 字段：dayjs 数组（与 PC TimePicker RangePicker 的 times 一致）
            const formArr = Array.isArray(value) && value.length === 2 ? [...value] : [null, null];
            formArr[side] = d;
            // allFieldValues：字符串数组（与 PC onChange 的 timeStrings 一致）
            const otherIdx = 1 - side;
            const other = Array.isArray(value) ? value[otherIdx] : null;
            const storeArr = ['', ''];
            storeArr[side] = formatted;
            storeArr[otherIdx] = formatValue(other, format);
            onChange && onChange(formArr);
            onValue && onValue(storeArr);
        } else {
            // Form 字段：dayjs 对象（与 PC TimePicker 的 time 一致）
            onChange && onChange(d);
            // allFieldValues：字符串（与 PC onChange 的 timeString 一致）
            onValue && onValue(formatted);
        }
        setVisible(false);
    };

    const ph0 = (placeholders && placeholders[0]) || '';
    const ph1 = (placeholders && placeholders[1]) || '';

    return (
        <>
            {isRange ? (
                <RangeRow
                    style={style}
                    v0={formatValue(value?.[0], format)}
                    v1={formatValue(value?.[1], format)}
                    ph0={ph0}
                    ph1={ph1}
                    icon={<ClockCircleOutlined />}
                    disabled={disabled}
                    onPickStart={() => openPicker('start')}
                    onPickEnd={() => openPicker('end')}
                />
            ) : (
                <TriggerSegment
                    style={style}
                    text={formatValue(value, format)}
                    placeholder={ph0}
                    icon={<ClockCircleOutlined />}
                    disabled={disabled}
                    onClick={() => openPicker('start')}
                />
            )}
            <MobilePicker
                visible={visible}
                columns={columns}
                value={currentValueArray}
                title={title}
                onClose={() => setVisible(false)}
                onConfirm={handleConfirm}
            />
        </>
    );
}
